/**
 * Projektlage — Antwort von `GET /api/kontor/projekt/{id}/lage`
 * (hetzner/webhook/kontor_projekt_lage.py, Plan Projekt-Tracking Etappe 1).
 *
 * Der Server rechnet die Phase aus Angeboten, Rechnungen und Terminen; der Kopf
 * der Projektseite zeigt sie nur. Rechnen ja, urteilen nein: `grund` nennt
 * Fakten, `schritt` ist genau eine Handlung.
 */

export type PhaseKey = "anfrage" | "angebot" | "planen" | "arbeit" | "abrechnen" | "zahlung" | "fertig" | "verloren" | "pausiert";

export type LageSchritt = {
  aktion:
    | "angebot_erstellen" | "nachhaken" | "angebot_angenommen" | "termine_planen" | "termin_oeffnen"
    | "rechnung_schreiben" | "zahlung_zuordnen" | "zahlungserinnerung" | "bewertung" | "verloren"
    | "abhaken" | "nacharbeit_frage";
  text: string;
  nummer?: string;
  datum?: string;
  /** Etappe 8: die Kundenmail, die nach Zusage klingt. */
  mail_id?: string;
  /** „arbeitsplan": der Schritt liegt im Arbeitsplan am Projekt (Etappe 6). */
  ort?: "arbeitsplan";
};

/** Der Arbeitsplan in Zahlen — `tage = 0`: das Projekt hat keinen Plan. */
export type LagePlan = {
  tage: number;
  ohne_datum?: number;
  ohne_datum_nr?: number[];
  kommend?: number;
  naechster?: string;
  naechster_nr?: number | null;
  leistungen?: number;
  leistungen_ohne_tag?: number;
  /** Etappe 7: abgehakt, nach allen Tagen noch offen, abrechnungsbereit. */
  leistungen_erledigt?: number;
  leistungen_offen?: number;
  abrechnungsbereit?: boolean;
};

export type LageMarke = { art: string; text: string; ton: "rot" | "gelb" | "gruen" | string };

export type ProjektLage = {
  projekt_id: string;
  titel: string;
  status: string;
  projektart: string;
  phase: PhaseKey;
  phase_name: string;
  /** Index im Band (0–6); null bei verloren/pausiert. */
  stufe: number | null;
  band: { phase: PhaseKey; name: string; zustand: "fertig" | "jetzt" | "offen" }[];
  grund: string;
  schritt: LageSchritt | null;
  marken: LageMarke[];
  /** Hinweise für die Aufräumliste — keine Urteile, nur Widersprüche in den Belegen. */
  hinweise: string[];
  status_soll: string;
  status_geschrieben: boolean;
  geld: {
    auftragswert: number; gestellt: number; bezahlt: number; offen: number; nicht_abgerechnet: number; herkunft_text: string;
    /** Vor Ort bar bezahlt (Migration 207) — 0/"" ohne Barzahlung. Ältere Server liefern die Felder nicht. */
    bar_brutto?: number; bar_netto?: number; bar_am?: string; schlussrechnung?: boolean;
  };
  termine: { kuenftig: number; naechster: string; vergangen: number; letzter: string };
  plan?: LagePlan;
  rechnungen_offen: { nummer: string; kind: string; brutto: number; faellig_am: string; datum: string }[];
  aufgaben_offen: number;
  stand: { titel: string; ereignis_am: string; erstellt_von: string } | null;
};

/** „2026-09-28" → „28.09." */
/**
 * „Vor Ort bar bezahlt" (07.10.2026) geht überall, wo noch abgerechnet werden könnte —
 * nicht bei verloren/pausiert, nicht nach einer Schlussrechnung, und bei „fertig"
 * nur, solange keine Rechnung gestellt ist (Fischer: von Hand abgeschlossen, 0 € gestellt).
 */
export function barMoeglich(lage: ProjektLage): boolean {
  return (lage.geld.bar_brutto ?? 0) > 0 || (lage.phase !== "verloren" && lage.phase !== "pausiert"
    && !lage.geld.schlussrechnung && (lage.phase !== "fertig" || lage.geld.gestellt <= 0));
}

/** Ohne Einsatz abrechnen (20.10.2026, „Jannasch Baumfällanträge"): beauftragt, aber noch kein Termin. */
export function ohneEinsatzMoeglich(lage: ProjektLage): boolean {
  return lage.phase === "planen" && lage.termine.kuenftig === 0 && lage.termine.vergangen === 0;
}

export function tagKurz(iso: string | null | undefined): string {
  if (!iso) return "";
  const [, m, d] = iso.slice(0, 10).split("-");
  return d && m ? `${d}.${m}.` : iso;
}

/** Wochentag + Datum für Termine: „Fr 09.10." */
export function tagMitWochentag(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return tagKurz(iso);
  return `${d.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "")} ${tagKurz(iso)}`;
}

export function istUeberfaellig(faelligIso: string, heute = new Date()): boolean {
  if (!faelligIso) return false;
  const h = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, "0")}-${String(heute.getDate()).padStart(2, "0")}`;
  return faelligIso.slice(0, 10) < h;
}

/** Eine Zeile aus `GET /api/kontor/projekte/phasen` — je laufendem Projekt (Etappe 3). */
export type ProjektPhaseZeile = {
  projekt_id: string;
  kunde_id: string;
  titel: string;
  projektart: string;
  phase: PhaseKey;
  phase_name: string;
  stufe: number | null;
  grund: string;
  schritt: LageSchritt | null;
  marken: LageMarke[];
  ueberfaellig: boolean;
};
